import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { PropsWithChildren } from 'react';
import {
  type ConsentChoice,
  type ConsentPreferences,
  readConsentChoice,
  saveConsentChoice,
} from '~/core/consent/choice';
import { stopBrowserSentry } from '~/core/sentry/initialize-browser-sentry';

const CONSENT_CHANGE_STORAGE_KEY = 'directordeck_consent_changed';

export type PrivacyTriggerPlacement = 'desktop' | 'compact' | 'all';

type ConsentContextValue = {
  choice: ConsentChoice | null;
  ready: boolean;
  save: (preferences: ConsentPreferences) => void;
  openPreferences: (opener: HTMLButtonElement) => void;
  registerPreferencesDialog: (
    open: (opener: HTMLButtonElement) => void,
  ) => () => void;
  registerPrivacyTrigger: (placement: PrivacyTriggerPlacement) => () => void;
  hasDesktopPrivacyTrigger: boolean;
  hasCompactPrivacyTrigger: boolean;
};

const ConsentContext = createContext<ConsentContextValue>({
  choice: null,
  ready: false,
  save: () => {},
  openPreferences: () => {},
  registerPreferencesDialog: () => () => {},
  registerPrivacyTrigger: () => () => {},
  hasDesktopPrivacyTrigger: false,
  hasCompactPrivacyTrigger: false,
});

export function ConsentProvider({ children }: PropsWithChildren) {
  const [choice, setChoice] = useState<ConsentChoice | null>(null);
  const [ready, setReady] = useState(false);
  const observedDiagnosticsGrantId = useRef<string | null>(null);
  const preferencesDialogOpener = useRef<
    ((opener: HTMLButtonElement) => void) | null
  >(null);
  const [privacyTriggerCounts, setPrivacyTriggerCounts] = useState({
    desktop: 0,
    compact: 0,
    all: 0,
  });

  const openPreferences = useCallback((opener: HTMLButtonElement) => {
    preferencesDialogOpener.current?.(opener);
  }, []);

  const registerPreferencesDialog = useCallback(
    (open: (opener: HTMLButtonElement) => void) => {
      preferencesDialogOpener.current = open;
      return () => {
        if (preferencesDialogOpener.current === open) {
          preferencesDialogOpener.current = null;
        }
      };
    },
    [],
  );

  const registerPrivacyTrigger = useCallback(
    (placement: PrivacyTriggerPlacement) => {
      setPrivacyTriggerCounts((counts) => ({
        ...counts,
        [placement]: counts[placement] + 1,
      }));
      return () => {
        setPrivacyTriggerCounts((counts) => ({
          ...counts,
          [placement]: counts[placement] - 1,
        }));
      };
    },
    [],
  );

  const applyChoice = useCallback((nextChoice: ConsentChoice | null) => {
    const nextGrantId = nextChoice?.diagnosticsGrantId ?? null;
    if (
      nextGrantId === null ||
      (observedDiagnosticsGrantId.current !== null &&
        observedDiagnosticsGrantId.current !== nextGrantId)
    )
      void stopBrowserSentry().catch(() => {});
    observedDiagnosticsGrantId.current = nextGrantId;
    setChoice(nextChoice);
  }, []);

  useEffect(() => {
    const initialChoice = readConsentChoice();
    observedDiagnosticsGrantId.current =
      initialChoice?.diagnosticsGrantId ?? null;
    setChoice(initialChoice);
    setReady(true);
    const refresh = () => applyChoice(readConsentChoice());
    const refreshFromStorage = (event: StorageEvent) => {
      if (event.key === CONSENT_CHANGE_STORAGE_KEY && event.newValue !== null)
        refresh();
    };
    window.addEventListener('focus', refresh);
    window.addEventListener('storage', refreshFromStorage);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('storage', refreshFromStorage);
    };
  }, [applyChoice]);

  const value = useMemo<ConsentContextValue>(
    () => ({
      choice,
      ready,
      openPreferences,
      registerPreferencesDialog,
      registerPrivacyTrigger,
      hasDesktopPrivacyTrigger:
        privacyTriggerCounts.desktop + privacyTriggerCounts.all > 0,
      hasCompactPrivacyTrigger:
        privacyTriggerCounts.compact + privacyTriggerCounts.all > 0,
      save: (preferences) => {
        const nextChoice = saveConsentChoice(preferences);
        applyChoice(nextChoice);
        // Cookies are shared across tabs, but have no change event. The
        // short-lived storage entry only tells other tabs to reread the cookie.
        try {
          window.localStorage.setItem(
            CONSENT_CHANGE_STORAGE_KEY,
            String(nextChoice.savedAt),
          );
          window.localStorage.removeItem(CONSENT_CHANGE_STORAGE_KEY);
        } catch {
          // Focus still refreshes consent when browser storage is unavailable.
        }
      },
    }),
    [
      choice,
      ready,
      openPreferences,
      registerPreferencesDialog,
      registerPrivacyTrigger,
      privacyTriggerCounts,
      applyChoice,
    ],
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent() {
  return useContext(ConsentContext);
}
